import React from "react";
import LegalPage from "@/pages/LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      lastUpdated="December 2025"
      testId="privacy-page"
    >
      <p>
        This Privacy Policy explains how{" "}
        <strong>Heritex Innovations Pvt. Ltd.</strong> ("<strong>Heritex</strong>",
        "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>")
        collects, uses, stores and protects personal information when you visit{" "}
        <a href="https://heritexinnovation.com">heritexinnovation.com</a> (the
        "<strong>Site</strong>") or engage us for services. We respect your
        privacy and comply with the Digital Personal Data Protection Act, 2023
        (India) and other applicable laws.
      </p>

      <h2>1. Who We Are</h2>
      <p>
        Heritex Innovations Pvt. Ltd. is a private limited company incorporated
        in India, registered in <strong>Lucknow, Uttar Pradesh</strong>, with
        our head office in <strong>Mumbai, Maharashtra</strong>. We are the
        "Data Fiduciary" / "Data Controller" for the personal information you
        share with us through the Site.
      </p>

      <h2>2. Information We Collect</h2>
      <h3>a) Information you provide directly</h3>
      <ul>
        <li><strong>Contact form data</strong> — your name, email address, phone number, company name, project type, budget range and the message you submit.</li>
        <li><strong>Engagement information</strong> — any information you provide during proposals, kickoffs or project work (e.g. business context, credentials shared for delivery, brand assets).</li>
        <li><strong>Communications</strong> — emails, WhatsApp messages or calls between you and us.</li>
      </ul>

      <h3>b) Information collected automatically</h3>
      <ul>
        <li><strong>Usage data</strong> — pages visited, time on page, referring URL, broad geographic region.</li>
        <li><strong>Device & technical data</strong> — IP address, browser type, operating system, device type.</li>
        <li><strong>Cookies & similar technologies</strong> — to keep the Site functional and (optionally) measure traffic. See Section 7.</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use your personal information to:</p>
      <ul>
        <li>Respond to your enquiry, prepare proposals and provide our services;</li>
        <li>Communicate with you about projects, invoices and support;</li>
        <li>Operate, secure and improve the Site;</li>
        <li>Comply with our legal, accounting and tax obligations;</li>
        <li>Send occasional updates about our work (only if you opt in or if you are an existing client);</li>
        <li>Detect, investigate and prevent fraud, abuse or security incidents.</li>
      </ul>

      <h2>4. Legal Bases</h2>
      <p>
        We process personal information based on (a) your consent (e.g. when
        you submit our contact form), (b) the necessity to perform a contract
        with you, (c) our legitimate business interests (e.g. running and
        improving the Site), and (d) compliance with applicable law.
      </p>

      <h2>5. Sharing Your Information</h2>
      <p>
        We do not sell your personal information. We may share it only with:
      </p>
      <ul>
        <li><strong>Trusted service providers</strong> who help us operate (e.g. cloud hosting, database, email, analytics, accounting), bound by confidentiality and security obligations;</li>
        <li><strong>Professional advisors</strong> (lawyers, auditors) where reasonably necessary;</li>
        <li><strong>Regulatory or government authorities</strong> if required by law or to protect our rights;</li>
        <li><strong>A successor entity</strong> in connection with a merger, acquisition or reorganisation of our business.</li>
      </ul>

      <h2>6. International Transfers</h2>
      <p>
        Some of our service providers may store or process data outside India
        (e.g. cloud hosting providers). Where this happens, we take reasonable
        steps to ensure that adequate safeguards are in place to protect your
        personal information.
      </p>

      <h2>7. Cookies</h2>
      <p>
        The Site uses a minimal set of cookies and similar technologies to
        keep core functionality working. We may also use privacy-respecting
        analytics to understand aggregate traffic patterns. You can disable
        non-essential cookies via your browser settings.
      </p>

      <h2>8. Data Retention</h2>
      <p>
        We retain personal information only for as long as necessary for the
        purposes set out in this Policy or as required by law. Contact-form
        submissions are typically retained for up to 24 months unless the
        enquiry results in an engagement, in which case records are kept for
        as long as required by accounting and tax laws (typically 8 years
        under Indian law).
      </p>

      <h2>9. Security</h2>
      <p>
        We implement reasonable technical and organisational measures to
        protect personal information against unauthorised access, alteration,
        disclosure or destruction. However, no system can be guaranteed 100%
        secure, and you share information with us at your own risk.
      </p>

      <h2>10. Your Rights</h2>
      <p>Subject to applicable law, you have the right to:</p>
      <ul>
        <li>Access the personal information we hold about you;</li>
        <li>Request correction of inaccurate or incomplete information;</li>
        <li>Request erasure of your information, subject to our legal retention obligations;</li>
        <li>Withdraw consent at any time (this will not affect prior lawful processing);</li>
        <li>Object to or restrict certain processing;</li>
        <li>Lodge a complaint with the relevant data protection authority.</li>
      </ul>
      <p>
        To exercise any of these rights, please contact us using the details
        below. We will respond within the timeframes required by applicable
        law.
      </p>

      <h2>11. Children</h2>
      <p>
        The Site is not directed to children under 18. We do not knowingly
        collect personal information from children. If you believe we have
        inadvertently done so, please contact us and we will delete it.
      </p>

      <h2>12. Third-Party Links</h2>
      <p>
        The Site may contain links to third-party websites. We are not
        responsible for the privacy practices of those websites — please
        review their policies separately.
      </p>

      <h2>13. Changes to this Policy</h2>
      <p>
        We may update this Policy from time to time. The "Last updated" date
        at the top reflects the most recent version. We encourage you to
        review this Policy periodically.
      </p>

      <h2>14. Contact & Grievance Officer</h2>
      <p>
        For privacy-related questions or to exercise your rights, please
        contact:
      </p>
      <p>
        <strong>Heritex Innovations Pvt. Ltd.</strong>
        <br />
        Registered Office: Lucknow, Uttar Pradesh, India
        <br />
        Head Office: Mumbai, Maharashtra, India
        <br />
        Phone / WhatsApp:{" "}
        <a href="tel:+919873117605">+91 98731 17605</a>
        <br />
        Email:{" "}
        <a href="mailto:privacy@heritexinnovation.com">privacy@heritexinnovation.com</a>
      </p>
    </LegalPage>
  );
}
